import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Modal, InputAdornment, IconButton, TextField, CircularProgress, TablePagination } from "@mui/material";
import { getCategoryList, getProducts } from "../../../Services/authService";
import CategoriesSlider from '../Inventory/CategorySlider';
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import CloseIcon from "@mui/icons-material/Close";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SearchIcon from '@mui/icons-material/Search';
import TempImage from '../../../assets/ProductImage.webp'
import AddIcon from '@mui/icons-material/Add';
import debounce from "lodash/debounce"; // Debounce for efficient search handling

const ProductSelectionModal = ({ open, onClose, onSelect, tags }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const searchBackground = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const dotsColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const plusColor = mode === 'dark' ? DARKCOLORS.BaseWhite : LIGHTCOLORS.BaseWhite;
  const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;
  const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  // Fetch categories on modal open
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const categoryData = await getCategoryList();
      setCategories(categoryData);

      if (categoryData.length > 0) {
        const initialCategoryId = categoryData[0]?.id;
        setSelectedCategory(initialCategoryId);
        fetchProducts(initialCategoryId, 0, ""); // Fetch products for the initial category
      }
    } catch (error) {
      console.error(t("vendingMachines.errorFetchingCategories"), error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch products for a category
  const fetchProducts = useCallback(
    async (categoryId, pageNum, search) => {
      setLoading(true);
      try {
        const productData = await getProducts(pageNum, rowsPerPage, categoryId, search);
        setProducts(productData.content || []);
        setTotalRows(productData.totalElements || 0);
      } catch (error) {
        console.error(t("vendingMachines.errorFetchingProducts"), error);
      } finally {
        setLoading(false);
      }
    },
    [rowsPerPage]
  );

  // Handle search input changes with debounce
  // const handleSearchChange = useCallback(
  //   debounce((value) => {
  //     setPageNumber(0); // Reset to the first page
  //     setProducts([]); // Clear current products
  //     fetchProducts(selectedCategory, 0, value); // Fetch products for the search term
  //   }, 300),
  //   [selectedCategory, fetchProducts]
  // );

  const handleSearchChange = useCallback(
    debounce((value) => {
      const trimmedValue = value.trim().toLowerCase();

      // Check if the search term matches a category
      const matchedCategory = categories.find(category =>
        category.name.toLowerCase().includes(trimmedValue)
      );

      if (matchedCategory) {
        // Redirect to matched category
        handleCategoryChange(matchedCategory.id);
      } else {
        // No matching category, perform product search
        setPage(0); // Reset to first page
        setProducts([]); // Clear current products
        fetchProducts(selectedCategory, 0, value); // Search within current category
      }
    }, 300),
    [categories, selectedCategory, fetchProducts]
  );

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    // console.log(t("vendingMachines.selectedCategoryId"), categoryId);

    setSelectedCategory(categoryId);
    setPage(0); // Reset pagination
    setProducts([]); // Clear current products
    fetchProducts(categoryId, 0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchProducts(selectedCategory, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchProducts(selectedCategory, 0);
  };

  // Fetch categories when modal opens
  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, fetchCategories]);

  // Function to get the tag icon based on the label
  const getTagIcon = (tagLabel) => {
    const tag = tags.find(tag => tag.label.toLowerCase() === tagLabel.toLowerCase());
    if (!tag || !tag.IconComponent) return null;

    const Icon = tag.IconComponent;
    const noSlash = ['vegan', 'vegetarian', 'high protein'].includes(tagLabel.toLowerCase());

    return (
      <span
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 35,
          height: 35,
          borderRadius: '50%',
          padding: '5px',
          backgroundColor: 'white', // or use theme
          overflow: 'hidden',
        }}
      >
        <Icon width="20" height="20" />

        {!noSlash && (
          <span
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '100%',
              height: '1px',
              backgroundColor: 'black', // Adjust to contrast with background
              transform: 'translate(-50%, -50%) rotate(130deg)',
              transformOrigin: 'center',
            }}
          />
        )}
      </span>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxHeight: "95%",
          overflowY: "auto",
          overflowX: "hidden",
          bgcolor: backgroundCard,
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
         
          '@media (max-width: 620px)': {
            width: "95%",
            p: 2,
          },
          '@media (max-width: 375px)': {
            width: "100%",
            p: 1.5,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", width: '100%', alignItems: "center", mb: 3 }}>
          <h2 id=" header-heading" className="addmachine-text-heading1" style={{ margin: "0px" }}>{t('inventory.inventory')}</h2>
          <Box className='desktop-search' style={{ display: "flex", alignItems: "center", gap: "3rem" }}>
            {/* Search Bar */}
            <TextField
              placeholder={t("membershipCards.search")}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ borderRadius: "24px", background: searchBackground, width: "calc(12rem + 12vw)" }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none", // Removes the border from the outline
                  },
                },
              }}
            />
            <IconButton sx={{ p: 0 }} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className='mobile-search'>
            <IconButton sx={{ p: 0 }} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box className='mobile-search' style={{ display: "none", width: '100%', alignItems: "center", }}>
          {/* Search Bar */}
          <TextField
            placeholder={t("membershipCards.search")}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{ borderRadius: "24px", background: searchBackground, width: "calc(12rem + 12vw)" }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none", // Removes the border from the outline
                },
              },
            }}
          />
        </Box>

        {/* Categories Slider */}
        <CategoriesSlider categories={categories} selectedCategory={selectedCategory} sendSelectedCategory={(category) => handleCategoryChange(category.id)} slotPage='slotPage' />


        {/* Product List */}
        <Box mb={2}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box sx={{
              display: "grid",
              padding: "10px",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "20px", // Space between items
              overflow: "auto",
              maxHeight: "58vh",
              // padding: "10px",
              '&::-webkit-scrollbar': {
                width: '10px !important',
                height: '8px !important',
                padding: '2px !important',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: scroll1,
                borderRadius: '10px',
                border: `1px solid ${scroll}`,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: scroll,
                borderRadius: '10px',
                width: '3px !important',

                border: '3px solid transparent',
                backgroundClip: 'padding-box',
                '&:hover': {
                  backgroundColor: scroll,
                },
              },
              "@media (max-width: 1200px)": { // For medium screens, 3 items per row
                gridTemplateColumns: "repeat(3, 1fr)"
              },
              "@media (max-width: 900px)": { // For medium-small screens, 2 items per row
                gridTemplateColumns: "repeat(2, 1fr)"
              },
              "@media (max-width: 600px)": { // For small screens, 1 item per row
                gridTemplateColumns: "1fr"
              },
            }}>
              {products.length > 0 ? (
                products.map((product) => (
                  <Box
                    sx={{
                      background: inputBoxColor,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                      overflow: "hidden",
                      cursor: 'pointer'
                    }}
                    className="product-border-sec"
                    onClick={() => onSelect(product)}
                  >
                    {/* Product Image */}
                    <Box sx={{ position: "relative" }}>
                      <img
                        src={product.imageUrl || TempImage}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = TempImage;
                        }}
                        className="product-border-sec12"
                      />
                      {/* Tags: Icons at the bottom-right corner */}
                      {/* {product?.tags && product?.tags?.length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "15px",
                            right: "10px",
                            display: "flex",
                            gap: "5px",
                          }}
                        >
                          {product?.tags.map((tag, index) => (
                            <Box
                              key={index}
                              sx={{
                                // backgroundColor: "#fff",
                                // borderRadius: "50%",
                                // padding: "5px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {getTagIcon(tag)}
                            </Box>
                          ))}
                        </Box>
                      )} */}
                    </Box>

                    {/* Product Details */}
                    <Box sx={{ padding: "5px 10px" }} sty>
                      {/* Product Name */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", gap: "10px" }}>
                        <p className="machine-name-text" style={{
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1, // Take available width
                          minWidth: 0 // Allow shrinking for ellipsis
                        }} >{product.productName}</p>
                        {/* Circle Background with "+" Icon */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "30px", // Size of the circle
                            height: "30px", // Size of the circle
                            borderRadius: "50%", // Make the background circular
                            backgroundColor: dotsColor, // Set the background color
                            cursor: "pointer"
                          }}
                        >
                          <AddIcon sx={{ color: plusColor, fontSize: "20px" }} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography>{t('vendingMachines.Noproducts')}</Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("membershipCards.RowsPerPage", "Rows Per Page")}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      </Box>
    </Modal>
  );
};

export default ProductSelectionModal;
